import React, { useMemo } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { CssBaseline, ThemeProvider, createTheme } from '@mui/material';
import { AuthProvider } from './hooks/AuthProvider';
import { useAuth } from './hooks/useAuth';
import { usePermissions } from './hooks/usePermissions';
import { ThemeModeProvider, useThemeMode } from './hooks/ThemeContext';
import { EasterEggProvider } from './hooks/EasterEggContext';
import { NotificationProvider } from './hooks/NotificationContext';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProjekteListPage from './pages/ProjekteListPage';
import ProjektDetailPage from './pages/ProjektDetailPage';
import KundenListPage from './pages/KundenListPage';
import KundeDetailPage from './pages/KundeDetailPage';
import MitarbeiterListPage from './pages/MitarbeiterListPage';
import MitarbeiterDetailPage from './pages/MitarbeiterDetailPage';
import AuditsListPage from './pages/AuditsListPage';
import AuditDetailPage from './pages/AuditDetailPage';
import AuditDemoModePage from './pages/AuditDemoModePage';
import FirewallReviewPage from './pages/FirewallReviewPage';
import SchwachstelleDetailPage from './pages/SchwachstelleDetailPage';
import VerwaltungPage from './pages/VerwaltungPage';
import AuditkalenderPage from './pages/AuditkalenderPage';
import KanbanPage from './pages/KanbanPage';
import RechnungenListPage from './pages/RechnungenListPage';
import RechnungDetailPage from './pages/RechnungDetailPage';
import AngeboteListPage from './pages/AngeboteListPage';
import AngebotDetailPage from './pages/AngebotDetailPage';
import RechnungTextvorlagenPage from './pages/RechnungTextvorlagenPage';
import UrlaubPage from './pages/UrlaubPage';
import UrlaubUebersichtPage from './pages/UrlaubUebersichtPage';
import AnwesenheitPage from './pages/AnwesenheitPage';
import ZeiterfassungPage from './pages/ZeiterfassungPage';
import ProfilPage from './pages/ProfilPage';
import MfaSetupPage from './pages/MfaSetupPage';
import PasswortVergessenPage from './pages/PasswortVergessenPage';
import PasswortResetPage from './pages/PasswortResetPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import HallOfFamePage from './pages/HallOfFamePage';
import DiskussionHomePage from './pages/DiskussionHomePage';
import DiskussionCategoryPage from './pages/DiskussionCategoryPage';
import DiskussionThreadPage from './pages/DiskussionThreadPage';
import DocsPage from './pages/DocsPage';
import ReviewQueuePage from './pages/ReviewQueuePage';
import BackupPage from './pages/BackupPage';
import BerechtigungsmatrixPage from './pages/BerechtigungsmatrixPage';
import SystemkonfigurationPage from './pages/SystemkonfigurationPage';
import DebugWartungPage from './pages/DebugWartungPage';
import DatenImportExportPage from './pages/DatenImportExportPage';
import PTAPage from './pages/PTAPage';
import WorkbenchPage from './pages/WorkbenchPage';
import WorkbenchNmapPage from './pages/WorkbenchNmapPage';
import WorkbenchSubfinderPage from './pages/WorkbenchSubfinderPage';
import WorkbenchCyberChefPage from './pages/WorkbenchCyberChefPage';
import WorkbenchRevShellPage from './pages/WorkbenchRevShellPage';
import WorkbenchJwtPage from './pages/WorkbenchJwtPage';
import WorkbenchTestsslPage from './pages/WorkbenchTestsslPage';
import WorkbenchDrawioPage from './pages/WorkbenchDrawioPage';
import WorkbenchNmapXmlPage from './pages/WorkbenchNmapXmlPage';
import UploadSessionsListPage from './pages/UploadSessionsListPage';
import UploadSessionDetailPage from './pages/UploadSessionDetailPage';
import PublicUploadPage from './pages/PublicUploadPage';
import EingangsbelegePage from './pages/EingangsbelegePage';
import RestrictedLayout from './components/layout/RestrictedLayout';
import SandboxFindingsListPage from './pages/sandbox/SandboxFindingsListPage';
import SandboxFindingPage from './pages/sandbox/SandboxFindingPage';
import SharedAuditPage from './pages/sandbox/SharedAuditPage';
import SharedFindingPage from './pages/sandbox/SharedFindingPage';
import SandboxApiDocsPage from './pages/sandbox/SandboxApiDocsPage';
import IsolierteUmgebungPage from './pages/IsolierteUmgebungPage';
import KaffeePage from './pages/KaffeePage';
import { CircularProgress, Box } from '@mui/material';
import * as P from './constants/permissions';

/* ─── Color palettes ───────────────────────────────────────────── */

export const darkColors = {
  bgDefault: '#181c34',
  bgPaper: '#212744',
  bgSidebar: '#1c203b',
  bgElevated: '#2c3358',
  bgInput: '#1a1f3c',
  accent: '#3dd47e',
  accentDark: '#2ba862',
  accentOrange: '#e0875a',
  textPrimary: '#e8edf3',
  textSecondary: '#8899aa',
  border: '#2e3560',
  borderLight: '#3b4370',
  hoverBg: '#2c3358',
  sidebarText: '#b0bec5',
  sidebarLabel: '#667088',
  sidebarIcon: '#7580a0',
  versionText: '#505878',
};

export const lightColors = {
  bgDefault: '#f0f2f5',
  bgPaper: '#ffffff',
  bgSidebar: '#ffffff',
  bgElevated: '#f5f7fa',
  bgInput: '#f8f9fb',
  accent: '#2ba862',
  accentDark: '#1e8a4e',
  accentOrange: '#d07040',
  textPrimary: '#1a2332',
  textSecondary: '#5f7082',
  border: '#dfe3e8',
  borderLight: '#c8cfd8',
  hoverBg: '#eef1f5',
  sidebarText: '#3a4a5a',
  sidebarLabel: '#6b7c8d',
  sidebarIcon: '#6b7c8d',
  versionText: '#8494a0',
};

export type StarkColors = typeof darkColors;

export const getColors = (mode: 'dark' | 'light'): StarkColors =>
  mode === 'dark' ? darkColors : lightColors;

/* ─── Theme builder ───────────────────────────────────────────── */

const buildTheme = (mode: 'dark' | 'light') => {
  const c = getColors(mode);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: c.accent,
        dark: c.accentDark,
        contrastText: mode === 'dark' ? '#181c34' : '#ffffff',
      },
      secondary: {
        main: c.accentOrange,
        contrastText: '#fff',
      },
      background: {
        default: c.bgDefault,
        paper: c.bgPaper,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
      },
      divider: c.border,
      error: { main: '#ef5350' },
      warning: { main: '#ffa726' },
      info: { main: '#42a5f5' },
      success: { main: c.accent },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700, color: c.accent },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: c.bgDefault,
            scrollbarColor: `${c.borderLight} ${c.bgDefault}`,
            '&::-webkit-scrollbar': { width: '8px' },
            '&::-webkit-scrollbar-track': { background: c.bgDefault },
            '&::-webkit-scrollbar-thumb': {
              background: c.borderLight,
              borderRadius: '4px',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            border: `1px solid ${c.border}`,
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
            fontWeight: 600,
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: `0 0 20px ${c.accent}33`,
            },
          },
          outlined: {
            borderColor: c.border,
            '&:hover': {
              borderColor: c.accent,
              backgroundColor: `${c.accent}10`,
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            border: `1px solid ${c.border}`,
            borderRadius: 12,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              backgroundColor: c.bgElevated,
              color: c.accent,
              fontWeight: 700,
              fontSize: '0.8rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderBottom: `1px solid ${c.border}`,
            },
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: `${c.hoverBg} !important`,
            },
            '& .MuiTableCell-root': {
              borderBottom: `1px solid ${c.border}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${c.border}`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              backgroundColor: c.bgInput,
              '& fieldset': {
                borderColor: c.border,
              },
              '&:hover fieldset': {
                borderColor: c.borderLight,
              },
              '&.Mui-focused fieldset': {
                borderColor: c.accent,
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: c.bgInput,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: c.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: c.borderLight,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: c.accent,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            border: `1px solid ${c.border}`,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            '&.Mui-selected': {
              color: c.accent,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: c.accent,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: c.bgElevated,
            border: `1px solid ${c.border}`,
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            '&.Mui-checked': {
              color: c.accent,
            },
            '&.Mui-checked + .MuiSwitch-track': {
              backgroundColor: c.accent,
            },
          },
        },
      },
    },
  });
};

/* ─── Route protection ─────────────────────────────────────────── */

/** Alte Adresse leitet auf /firewall/bewertung um und übernimmt die Query. */
const FirewallBewertungRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/audits/${id}/firewall/bewertung${search}`} replace />;
};

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Enforce MFA setup: if admin required MFA but user hasn't set it up yet
  if (user.mfa_pflicht && !user.has_2fa) {
    return <Navigate to="/mfa-einrichten" replace />;
  }

  return <>{children}</>;
};

const PermissionRoute: React.FC<{ permission: string | string[]; children: React.ReactNode }> = ({ permission, children }) => {
  const { hasPermission, hasAnyPermission } = usePermissions();
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const allowed = Array.isArray(permission)
    ? hasAnyPermission(...permission)
    : hasPermission(permission);

  if (!user || !allowed) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: 'background.default' }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  // Check if user needs forced MFA setup
  const needsMfaSetup = user && user.mfa_pflicht && !user.has_2fa;

  // Eingeschraenkte Sitzung (Restricted-Login): ausschliesslich die isolierte Umgebung
  if (user && user.scope === 'restricted') {
    return (
      <Routes>
        <Route path="/login" element={<Navigate to="/sandbox" replace />} />
        <Route element={<RestrictedLayout />}>
          <Route path="/sandbox" element={<SandboxFindingsListPage />} />
          <Route path="/sandbox/findings/neu" element={<SandboxFindingPage mode="restricted" />} />
          <Route path="/sandbox/findings/:id" element={<SandboxFindingPage mode="restricted" />} />
          <Route path="/sandbox/shared/audits/:id" element={<SharedAuditPage />} />
          <Route path="/sandbox/shared/findings/:id" element={<SharedFindingPage />} />
          <Route path="/sandbox/api-docs" element={<SandboxApiDocsPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/sandbox" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={user ? (needsMfaSetup ? <Navigate to="/mfa-einrichten" replace /> : <Navigate to="/" replace />) : <LoginPage />}
      />
      <Route
        path="/mfa-einrichten"
        element={!user ? <Navigate to="/login" replace /> : (!needsMfaSetup ? <Navigate to="/" replace /> : <MfaSetupPage />)}
      />
      <Route path="/passwort-vergessen" element={<PasswortVergessenPage />} />
      <Route path="/passwort-reset" element={<PasswortResetPage />} />
      <Route path="/passwort-reset/:token" element={<PasswortResetPage />} />
      <Route path="/upload/:token" element={<PublicUploadPage />} />
      <Route
        path="/audits/:id/demo"
        element={
          <ProtectedRoute>
            <PermissionRoute permission={P.AUDITS_VIEW}>
              <AuditDemoModePage />
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/audits/:id/firewall/bewertung"
        element={
          <ProtectedRoute>
            <PermissionRoute permission={P.AUDITS_VIEW}>
              <FirewallReviewPage mode="swipe" />
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route path="/audits/:id/firewall/swipe" element={<FirewallBewertungRedirect />} />
      <Route
        path="/audits/:id/firewall/final"
        element={
          <ProtectedRoute>
            <PermissionRoute permission={P.AUDITS_VIEW}>
              <FirewallReviewPage mode="final" />
            </PermissionRoute>
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        {/* Mein Bereich */}
        <Route path="/" element={<DashboardPage />} />
        <Route path="/kaffee" element={<KaffeePage />} />
        <Route path="/zeiterfassung" element={<ZeiterfassungPage />} />
        <Route path="/urlaub" element={<UrlaubPage />} />
        <Route path="/anwesenheit" element={<PermissionRoute permission={P.ANWESENHEIT_VIEW}><AnwesenheitPage /></PermissionRoute>} />
        <Route path="/hall-of-fame" element={<HallOfFamePage />} />
        <Route
          path="/diskussion"
          element={
            <PermissionRoute permission={P.DISKUSSION_VIEW}>
              <Outlet />
            </PermissionRoute>
          }
        >
          <Route index element={<DiskussionHomePage />} />
          <Route path="kategorie/:id" element={<DiskussionCategoryPage />} />
          <Route path="thread/:id" element={<DiskussionThreadPage />} />
        </Route>
        <Route path="/profil" element={<ProfilPage />} />
        <Route path="/isolierte-umgebung" element={<IsolierteUmgebungPage />} />
        <Route path="/isolierte-umgebung/findings/:id" element={<SandboxFindingPage mode="full" />} />

        {/* Projekte & Audits */}
        <Route path="/projekte" element={<PermissionRoute permission={P.PROJEKTE_VIEW}><ProjekteListPage /></PermissionRoute>} />
        <Route path="/projekte/:id" element={<PermissionRoute permission={P.PROJEKTE_VIEW}><ProjektDetailPage /></PermissionRoute>} />
        <Route path="/kanban" element={<PermissionRoute permission={P.PROJEKTE_VIEW}><KanbanPage /></PermissionRoute>} />
        <Route path="/auditkalender" element={<PermissionRoute permission={P.PROJEKTE_VIEW}><AuditkalenderPage /></PermissionRoute>} />
        <Route path="/audits" element={<PermissionRoute permission={P.AUDITS_VIEW}><AuditsListPage /></PermissionRoute>} />
        <Route path="/audits/:id" element={<PermissionRoute permission={P.AUDITS_VIEW}><AuditDetailPage /></PermissionRoute>} />
        <Route path="/schwachstellen/:id" element={<PermissionRoute permission={P.SCHWACHSTELLEN_VIEW}><SchwachstelleDetailPage /></PermissionRoute>} />
        <Route path="/reviews" element={<PermissionRoute permission={P.SCHWACHSTELLEN_REVIEW}><ReviewQueuePage /></PermissionRoute>} />

        {/* Verwaltung */}
        <Route path="/angebote" element={<PermissionRoute permission={P.ANGEBOTE_VIEW}><AngeboteListPage /></PermissionRoute>} />
        <Route path="/angebote/:id" element={<PermissionRoute permission={P.ANGEBOTE_VIEW}><AngebotDetailPage /></PermissionRoute>} />
        <Route path="/rechnungen/textvorlagen" element={<PermissionRoute permission={P.RECHNUNGEN_SETTINGS}><RechnungTextvorlagenPage /></PermissionRoute>} />
        <Route path="/rechnungen" element={<PermissionRoute permission={P.RECHNUNGEN_VIEW}><RechnungenListPage /></PermissionRoute>} />
        <Route path="/rechnungen/:id" element={<PermissionRoute permission={P.RECHNUNGEN_VIEW}><RechnungDetailPage /></PermissionRoute>} />
        <Route
          path="/eingangsbelege"
          element={
            <PermissionRoute permission={[P.EINGANGSBELEGE_OWN, P.EINGANGSBELEGE_VIEW_ALL, P.EINGANGSBELEGE_MANAGE]}>
              <EingangsbelegePage />
            </PermissionRoute>
          }
        />
        <Route path="/kunden" element={<PermissionRoute permission={P.KUNDEN_VIEW}><KundenListPage /></PermissionRoute>} />
        <Route path="/kunden/:id" element={<PermissionRoute permission={P.KUNDEN_VIEW}><KundeDetailPage /></PermissionRoute>} />
        <Route path="/mitarbeiter" element={<PermissionRoute permission={P.MITARBEITER_VIEW}><MitarbeiterListPage /></PermissionRoute>} />
        <Route path="/mitarbeiter/:id" element={<PermissionRoute permission={P.MITARBEITER_VIEW}><MitarbeiterDetailPage /></PermissionRoute>} />
        <Route path="/urlaub-uebersicht" element={<PermissionRoute permission={P.URLAUB_VIEW_ALL}><UrlaubUebersichtPage /></PermissionRoute>} />
        <Route path="/verwaltung" element={<PermissionRoute permission={P.VERWALTUNG_STAMMDATEN}><VerwaltungPage /></PermissionRoute>} />
        <Route path="/daten" element={<PermissionRoute permission={P.DATA_EXPORT}><DatenImportExportPage /></PermissionRoute>} />
        <Route path="/kundenuploads" element={<PermissionRoute permission={P.UPLOAD_SESSIONS_VIEW}><UploadSessionsListPage /></PermissionRoute>} />
        <Route path="/kundenuploads/:id" element={<PermissionRoute permission={P.UPLOAD_SESSIONS_VIEW}><UploadSessionDetailPage /></PermissionRoute>} />

        {/* Admin */}
        <Route path="/systemkonfiguration" element={<PermissionRoute permission={P.VERWALTUNG_MANAGE}><SystemkonfigurationPage /></PermissionRoute>} />
        <Route path="/email-templates" element={<PermissionRoute permission={P.VERWALTUNG_MANAGE}><EmailTemplatesPage /></PermissionRoute>} />
        <Route path="/backups" element={<PermissionRoute permission={P.VERWALTUNG_MANAGE}><BackupPage /></PermissionRoute>} />
        <Route path="/berechtigungsmatrix" element={<PermissionRoute permission={P.VERWALTUNG_MANAGE}><BerechtigungsmatrixPage /></PermissionRoute>} />
        <Route path="/debug-wartung" element={<PermissionRoute permission={P.VERWALTUNG_MANAGE}><DebugWartungPage /></PermissionRoute>} />
        <Route path="/pta" element={<PermissionRoute permission={P.WORKBENCH_USE}><PTAPage /></PermissionRoute>} />
        <Route path="/workbench" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchPage /></PermissionRoute>} />
        <Route path="/workbench/pta" element={<PermissionRoute permission={P.WORKBENCH_USE}><PTAPage /></PermissionRoute>} />
        <Route path="/workbench/nmap" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchNmapPage /></PermissionRoute>} />
        <Route path="/workbench/subfinder" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchSubfinderPage /></PermissionRoute>} />
        <Route path="/workbench/cyberchef" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchCyberChefPage /></PermissionRoute>} />
        <Route path="/workbench/revshell" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchRevShellPage /></PermissionRoute>} />
        <Route path="/workbench/jwt" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchJwtPage /></PermissionRoute>} />
        <Route path="/workbench/testssl" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchTestsslPage /></PermissionRoute>} />
        <Route path="/workbench/drawio" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchDrawioPage /></PermissionRoute>} />
        <Route path="/workbench/nmap-xml" element={<PermissionRoute permission={P.WORKBENCH_USE}><WorkbenchNmapXmlPage /></PermissionRoute>} />
        <Route path="/docs" element={<DocsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/* ─── Inner app (has access to ThemeMode context) ──────────────── */

const ThemedApp: React.FC = () => {
  const { mode } = useThemeMode();
  const theme = useMemo(() => buildTheme(mode), [mode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <BrowserRouter>
          <AuthProvider>
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </NotificationProvider>
    </ThemeProvider>
  );
};

/* ─── Root ─────────────────────────────────────────────────────── */

const App: React.FC = () => {
  return (
    <ThemeModeProvider>
      <EasterEggProvider>
        <ThemedApp />
      </EasterEggProvider>
    </ThemeModeProvider>
  );
};

export default App;
